import globalAxios from 'axios';
import { getEnv } from '../env/environment.js';
import { DefaultApi as DefaultApi$1 } from '../generated/cart-api/api.js';
import { Configuration as Configuration$1 } from '../generated/cart-api/configuration.js';
import { DefaultApi } from '../generated/cart-api-v2/api.js';
import { Configuration } from '../generated/cart-api-v2/configuration.js';
import { findArticleById, extractVersionFromCart, convertCartToV1 } from '../utils/cartUtils.js';
import { captureException } from './sentryService.js';
import { getCartApiVersion } from './stateService.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
let cartApi;
let cartApiStaticVersion;
class CartError extends Error {
    code;
    popover;
    constructor({ message, code, popover, }) {
        super(message);
        this.name = 'CartError';
        this.code = code;
        this.popover = popover;
    }
}
const isAxiosError = (error) => {
    return !!error.isAxiosError;
};
const isApiError = (error) => {
    return (isAxiosError(error) &&
        !!error.response?.data?.code &&
        !!error.response?.data?.message);
};
const buildError = (error, popover) => {
    if (isApiError(error)) {
        const { response } = error;
        captureException(error);
        return new CartError({ ...response.data, popover });
    }
    return error;
};
const getCartApi = async (stage, cartVersion) => {
    const cartApiVersion = cartVersion || (await getCartApiVersion(stage));
    if (cartApi && cartApiStaticVersion === cartApiVersion) {
        return cartApi;
    }
    else if (cartApiVersion === 'v2' && (!cartApi || cartApiStaticVersion === 'v1')) {
        cartApi = new DefaultApi(new Configuration({ basePath: getEnv(stage).CHECKOUT_API_BASE_PATH }), undefined, globalAxios);
        cartApiStaticVersion = 'v2';
    }
    else if (cartApiVersion === 'v1' && (!cartApi || cartApiStaticVersion === 'v2')) {
        cartApi = new DefaultApi$1(new Configuration$1({ basePath: getEnv(stage).CHECKOUT_API_BASE_PATH }), undefined, globalAxios);
        cartApiStaticVersion = 'v1';
    }
    else ;
    return cartApi;
};
const getCart = async ({ stage, sid, cartVersion, }) => {
    try {
        const newCartApi = await getCartApi(stage, cartVersion);
        const { data } = await newCartApi.getCartBySid(sid);
        return data;
    }
    catch (error) {
        return buildError(error);
    }
};
const changeArticleQuantity = async ({ stage, sid, request, popover, previousQuantity, cartVersion, }) => {
    try {
        let requestedQuantity = request.quantity || 0;
        requestedQuantity = requestedQuantity < 0 ? 0 : requestedQuantity;
        const cleanedRequest = {
            ...request,
            quantity: requestedQuantity,
        };
        const { data } = await (await getCartApi(stage, cartVersion)).changeArticleQuantity(sid, cleanedRequest);
        const cartArticle = findArticleById(data, cleanedRequest.articleId);
        const ret = {
            cart: data,
            status: computeStatus({
                previousQuantity,
                requestedQuantity,
                responseQuantity: cartArticle?.quantity || 0,
                maxQuantity: cartArticle?.maxQuantity || Number.MAX_SAFE_INTEGER,
            }),
        };
        if (ret.status === 'UNCHANGED') {
            const cartMessage = ret.cart.cartMessages?.find(m => m.code === 'CHECKOUT_STOCK_CART_ITEM_REMOVED');
            if (cartMessage) {
                throw new Error(cartMessage.localizedMessage);
            }
        }
        return ret;
    }
    catch (error) {
        throw buildError(error, popover);
    }
};
const addSavingsPlanArticle = async ({ stage, sid, articleId, cartVersion, }) => {
    try {
        const { data } = await (await getCartApi(stage, cartVersion)).addSavingsPlanArticle(sid, String(articleId));
        return data;
    }
    catch (error) {
        throw buildError(error);
    }
};
const deleteSavingsPlanArticle = async ({ stage, sid, cartVersion, }) => {
    try {
        const { data } = await (await getCartApi(stage, cartVersion)).deleteSavingsPlanArticle(sid);
        return data;
    }
    catch (error) {
        throw buildError(error);
    }
};
const addCoupon = async ({ stage, sid, couponCode, cartVersion, }) => {
    try {
        const { data } = await (await getCartApi(stage, cartVersion)).addCouponWithSid(sid, couponCode);
        return data;
    }
    catch (error) {
        throw buildError(error);
    }
};
const removeCoupon = async ({ stage, sid, couponCode, cartVersion, }) => {
    try {
        const { data } = await (await getCartApi(stage, cartVersion)).removeCouponWithSid(sid, couponCode);
        return data;
    }
    catch (error) {
        throw buildError(error);
    }
};
const computeStatus = ({ previousQuantity, requestedQuantity, responseQuantity, maxQuantity, }) => {
    let status;
    if (responseQuantity === previousQuantity) {
        if (responseQuantity === maxQuantity) {
            status = 'ALREADY_MAX_QUANTITY';
        }
        else {
            status = 'UNCHANGED';
        }
    }
    else if (responseQuantity > previousQuantity) {
        if (responseQuantity < requestedQuantity) {
            status = 'ADDED_TO_MAX_QUANTITY';
        }
        else {
            status = 'ADDED';
        }
    }
    // responseQuantity < previousQuantity
    else {
        if (responseQuantity === 0) {
            status = 'DELETED';
        }
        else {
            status = 'REDUCED';
        }
    }
    return status;
};
const computeSyncedCart = async (oldCart, newCart, stage, sid) => {
    if (oldCart && newCart) {
        if (extractVersionFromCart(newCart) === 'v2' && extractVersionFromCart(oldCart) === 'v1') {
            return convertCartToV1(newCart);
        }
        else if (extractVersionFromCart(newCart) === 'v1' &&
            extractVersionFromCart(oldCart) === 'v2' &&
            sid &&
            stage) {
            return await getCart({
                stage,
                sid,
                cartVersion: 'v2',
            });
        }
    }
    return newCart;
};

export { CartError, addCoupon, addSavingsPlanArticle, changeArticleQuantity, computeStatus, computeSyncedCart, deleteSavingsPlanArticle, getCart, removeCoupon };
//# sourceMappingURL=cartService.js.map
